import { AreaField } from './AreaField';
import { RoomsField } from './RoomsField';
import { BedroomsField } from './BedroomsField';
import { FloorField } from './FloorField';
import { FloorNumericField } from './FloorNumericField';
import { FloorLevelsField } from './FloorLevelsField';
import { GardenPlotBalconyField } from './GardenPlotBalconyField';
import { GardenOrPlotField } from './GardenOrPlotField';
import { TerraceField } from './TerraceField';
import { ParkingField } from './ParkingField';
import { PlotField } from './PlotField';
import { ExposureField } from './ExposureField';
import { PriceField } from './PriceField';
import { PricePerMeterField } from './PricePerMeterField';

/**
 * Field Registry - wszystkie dostępne pola
 * Singleton instances - każda klasa jest instancjonowana raz
 */
const FIELD_REGISTRY = {
  'area': new AreaField(),
  'rooms': new RoomsField(),
  'bedrooms': new BedroomsField(),
  'floor': new FloorField(),
  'floor_numeric': new FloorNumericField(),
  'floor_levels': new FloorLevelsField(),
  'garden_plot_balcony': new GardenPlotBalconyField(),
  'garden_or_plot': new GardenOrPlotField(),
  'terrace': new TerraceField(),
  'parking': new ParkingField(),
  'plot': new PlotField(),
  'exposure': new ExposureField(),
  'price': new PriceField(),
  'price_per_meter': new PricePerMeterField(),
};

/**
 * Domyślne konfiguracje
 */
const DEFAULT_LIST_FIELDS = ['floor', 'area', 'garden_plot_balcony', 'rooms', 'price'];
const DEFAULT_CARD_FIELDS = ['area', 'rooms', 'floor', 'garden_or_plot', 'terrace', 'parking'];

/**
 * Parse field configuration from database
 * Supports both simple array and advanced object format
 * @param {Object|Array} config - Config from database
 * @returns {Array} Normalized field configs [{ type: string, params: {} }]
 */
const parseFieldConfig = (config) => {
  if (!config) return null;

  // Handle simple array format: ["area", "rooms", "floor"]
  if (Array.isArray(config)) {
    return config.map(type => ({ type, params: {} }));
  }

  // Handle object format: { fields: [...] }
  if (config.fields) {
    return config.fields.map(field => {
      // Simple string in array
      if (typeof field === 'string') {
        return { type: field, params: {} };
      }
      // Object with type and params
      return {
        type: field.type,
        params: field.params || {}
      };
    });
  }

  return null;
};

/**
 * Get field configuration for widget type
 * @param {Object} investmentData - Investment data from database
 * @param {string} widgetType - 'list' or 'card'
 * @returns {Array} Array of { field: BaseField, params: {} }
 */
export const getFieldsConfig = (investmentData, widgetType = 'card') => {
  const configKey = widgetType === 'list'
    ? 'widget_property_details_list'
    : 'widget_property_details_card';

  const dbConfig = investmentData?.[configKey];
  const parsedConfig = parseFieldConfig(dbConfig);

  // Use defaults if no config
  const fieldConfigs = parsedConfig ||
    (widgetType === 'list' ? DEFAULT_LIST_FIELDS : DEFAULT_CARD_FIELDS)
      .map(type => ({ type, params: {} }));

  return fieldConfigs
    .map(({ type, params }) => {
      const field = FIELD_REGISTRY[type];
      return field ? { field, params } : null;
    })
    .filter(Boolean);
};

/**
 * Render field for specific widget type
 * @param {BaseField} field - Field instance
 * @param {Object} property - Property object
 * @param {string} widgetType - 'list' or 'card'
 * @returns {{ label: string, value: string|number }}
 */
export const renderField = (field, property, widgetType, params = {}) => {
  if (widgetType === 'list') {
    return field.renderForList(property, params);
  }
  return field.renderForCard(property, params);
};

/**
 * Get field icon (for card view)
 * @param {BaseField} field - Field instance
 * @returns {string|null} Icon path
 */
export const getFieldIcon = (field) => {
  return field.icon || null;
};

/**
 * Get all available field keys (for documentation/admin panel)
 * @returns {string[]} Array of field keys
 */
export const getAvailableFieldKeys = () => {
  return Object.keys(FIELD_REGISTRY);
};
